import { MutableRefObject, useRef, useState } from 'react'
import FocusLock from 'react-focus-lock'
import cx from 'classnames'

import { SvgIcon } from '@cinch-labs/design-system'
import { StoryblokImage } from '@cinch-nx/data-storyblok'

import { Modal, useModal } from '../modal'
import { CarReviewGallery } from '../car-review-gallery'

import styles from './gallery-modal.module.css'
import { SlideChangeDetails } from '../../types/car-review-page'
import { useVehicleGallery } from '../../hooks'

interface Props {
  images: StoryblokImage[]
}

export function GalleryModal({ images = [] }: Props) {
  const { close, isOpen } = useModal()
  const { imageIndex } = useVehicleGallery()
  const prevFocusedElement = useRef<Element | null>(null)
  const closeButton = useRef<HTMLButtonElement | null>(null)
  const [currentSlide, setCurrentSlide] = useState(0)

  const updateCurrentPagination = (details: SlideChangeDetails) => {
    if (details.activeIndex !== undefined) {
      setCurrentSlide(details.activeIndex)
    }
  }

  return (
    <Modal
      className={styles.galleryModal}
      onOpen={() => disableFocus(prevFocusedElement, closeButton)}
      onClose={() => resetFocus(prevFocusedElement)}
    >
      <FocusLock disabled={!isOpen} className={styles.fullWidth}>
        <div className={styles.galleryModalInner} aria-label="image-gallery">
          <header className={cx(styles.galleryModalHeader, styles.uiColours)}>
            <label>
              {currentSlide} / {images.length}
            </label>
            <div>
              <button
                className={styles.galleryModalCloseButton}
                onClick={() => close()}
                aria-label="Close"
                ref={closeButton}
              >
                <SvgIcon name="cross" size={24} />
              </button>
            </div>
          </header>
          <CarReviewGallery
            images={images}
            updateGalleryPaginationNumber={updateCurrentPagination}
            imageIndex={imageIndex}
          />
        </div>
      </FocusLock>
    </Modal>
  )
}

function disableFocus(
  prevFocusedElementRef: MutableRefObject<Element | null>,
  closeButtonRef: MutableRefObject<HTMLButtonElement | null>,
) {
  prevFocusedElementRef.current = document.activeElement
  if (closeButtonRef.current) {
    closeButtonRef.current.focus()
    document.body.style.overflow = 'hidden'
  }
}

function resetFocus(prevFocusedElementRef: MutableRefObject<Element | null>) {
  document.body.style.overflow = 'unset'
  document.body.setAttribute('aria-hidden', 'false')
  if (isHtmlElement(prevFocusedElementRef.current)) {
    prevFocusedElementRef.current.focus()
  }
}

function isHtmlElement(input: Element | null): input is HTMLElement {
  return !!input && Object.prototype.hasOwnProperty.call(input, 'focus')
}
